import React, { useMemo, useState } from 'react';
import { Check, Trash2, X, Landmark, Plus, RotateCcw, Pencil } from 'lucide-react';
import api from '../../api/client';
import InFinestra from '../../components/InFinestra';
import { testoRisposta, colonneRisposte } from './RisposteTabella';
import { s } from './FormLista';

// Strumenti solo staff di un form (26/10/2026): Pagamento (quota, chi ha pagato, bonifici della banca
// proposti) e Statistiche (conteggi delle risposte a scelta). Chi compila dal link non vede niente.

const euro = (n) => `${(+n || 0).toLocaleString('it-IT', { minimumFractionDigits: (+n || 0) % 1 ? 2 : 0, maximumFractionDigits: 2 })} €`;
const oggi = () => new Date().toISOString().slice(0, 10);
const dataIt = (d) => (d ? d.slice(0, 10).split('-').reverse().join('/') : '');

export const STATI_PAGAMENTO = {
  DA_PAGARE: { l: 'DA PAGARE', c: '#EF4444' },
  PARZIALE: { l: 'PARZIALE', c: '#F59E0B' },
  PAGATO: { l: 'PAGATO', c: '#22C55E' },
  NIENTE_DOVUTO: { l: 'NIENTE', c: '#8E8E93' },
};
const METODI = [['BONIFICO', 'Bonifico'], ['CONTANTI', 'Contanti'], ['SATISPAY', 'Satispay'], ['ALTRO', 'Altro']];
const LIVELLO = { ALTA: '#22C55E', MEDIA: '#F59E0B', BASSA: '#8E8E93' };

export function BadgePagamento({ p }) {
  if (!p) return null;
  const st = STATI_PAGAMENTO[p.stato] || STATI_PAGAMENTO.DA_PAGARE;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
      <span style={{ ...s.badge, color: st.c, borderColor: `${st.c}55`, background: `${st.c}12` }}>{st.l}</span>
      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-dim)' }}>{euro(p.pagato)} / {euro(p.quota)}</span>
      {p.proposte?.length > 0 && <span title="Bonifico della banca proposto" style={{ display: 'inline-flex', color: '#3B82F6' }}><Landmark size={13} /></span>}
    </span>
  );
}

// ── scheda STRUMENTI nell'editor ─────────────────────────────────────────────────────────
export function Strumenti({ f, puoModificare, onSalvato }) {
  const iniziale = f.strumenti || {};
  const [pag, setPag] = useState(!!iniziale.pagamento);
  const [quota, setQuota] = useState(iniziale.pagamento?.quota ?? '');
  const [quotaCorsisti, setQuotaCorsisti] = useState(iniziale.pagamento?.quota_corsisti ?? '');
  const [parole, setParole] = useState(iniziale.pagamento?.parole ?? '');
  const [stat, setStat] = useState(!!iniziale.statistiche);
  const [stato, setStato] = useState({ salvando: false, err: '', ok: false });
  const riga = { display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: 'var(--font-size-sm)', cursor: 'pointer' };

  const salva = async () => {
    setStato({ salvando: true, err: '', ok: false });
    try {
      await api.put(`/form/${f.id}/strumenti`, { pagamento: pag ? { quota, quota_corsisti: quotaCorsisti, parole } : null, statistiche: stat });
      setStato({ salvando: false, err: '', ok: true });
      setTimeout(() => setStato(x => ({ ...x, ok: false })), 1500);
      onSalvato();
    } catch (e) { setStato({ salvando: false, err: e.response?.data?.error || 'Non salvato', ok: false }); }
  };

  return (
    <fieldset disabled={!puoModificare} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      <p style={{ ...s.help, marginBottom: '14px', fontSize: 'var(--font-size-sm)' }}>
        Strumenti in più per lo staff, da accendere solo quando servono. <b>Chi compila il form non li vede</b>: compaiono solo nella scheda Risposte.
      </p>
      <div style={s.card}>
        <div style={s.cardTitle}>PAGAMENTO</div>
        <label style={riga}><input type="checkbox" checked={pag} onChange={e => setPag(e.target.checked)} style={{ accentColor: '#F4600C', marginTop: '3px' }} />
          <span>Segna chi ha pagato<span style={{ display: 'block', ...s.help }}>Nella tabella delle risposte compare la colonna PAGAMENTO: quota, quanto ha pagato, come e quando, con il riepilogo di quanto manca in cima. Si registra a mano oppure collegando il bonifico che il gestionale trova in banca.</span></span></label>
        {pag && <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginTop: '14px' }}>
            <div><label style={s.label}>QUOTA (€)</label><input style={s.inp} inputMode="decimal" value={quota} onChange={e => setQuota(e.target.value)} placeholder="es. 40" /></div>
            <div><label style={s.label}>QUOTA CORSISTI (€, facoltativa)</label><input style={s.inp} inputMode="decimal" value={quotaCorsisti} onChange={e => setQuotaCorsisti(e.target.value)} placeholder="uguale alla quota" /></div>
            <div><label style={s.label}>PAROLE NELLA CAUSALE (facoltative)</label><input style={s.inp} value={parole} onChange={e => setParole(e.target.value)} placeholder="es. club series" /></div>
          </div>
          <p style={s.help}>
            <b>Quota corsisti</b>: chi è in un gruppo del Planning corsi della stagione (riconosciuto dalla scheda anagrafica, dal codice fiscale o da nome e cognome) paga questa; gli altri la quota normale. Lasciala vuota se è uguale per tutti. Su ogni risposta la quota si può sempre cambiare a mano.<br />
            <b>Bonifici</b>: il gestionale propone sulla risposta i bonifici arrivati in banca con il <b>cognome</b> della persona nella causale o nel nome di chi paga, più queste parole oppure l'importo giusto. Niente si collega da solo: confermi tu con COLLEGA o scarti con la X. Le proposte le vede solo chi ha anche il permesso sui movimenti bancari.
          </p>
        </>}
      </div>
      <div style={s.card}>
        <div style={s.cardTitle}>STATISTICHE</div>
        <label style={riga}><input type="checkbox" checked={stat} onChange={e => setStat(e.target.checked)} style={{ accentColor: '#F4600C', marginTop: '3px' }} />
          <span>Conta le risposte<span style={{ display: 'block', ...s.help }}>Nella scheda Risposte compare la vista STATISTICHE: per ogni domanda a scelta quante persone hanno risposto cosa, con le barre. Si può dividere per un'altra domanda (es. taglie divise per serie) per ordinare il materiale. Con il pagamento acceso ci sono anche gli incassi.</span></span></label>
      </div>
      {puoModificare && (
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'flex-end' }}>
          {stato.err && <span style={{ color: '#EF4444', fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>{stato.err}</span>}
          {stato.ok && <span style={s.meta}>Salvato</span>}
          <button type="button" style={s.btnPri} disabled={stato.salvando} onClick={salva}><Check size={14} /> {stato.salvando ? 'SALVO…' : 'SALVA STRUMENTI'}</button>
        </div>
      )}
    </fieldset>
  );
}

// ── riepilogo incassi sopra la tabella ───────────────────────────────────────────────────
export function RiepilogoPagamenti({ dati }) {
  if (!dati?.attivo) return null;
  const r = dati.riepilogo;
  const box = (l, v, c) => (
    <div style={{ flex: '1 1 120px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '10px 14px' }}>
      <div style={s.label}>{l}</div>
      <div style={{ fontSize: '20px', fontWeight: 800, color: c }}>{v}</div>
    </div>
  );
  return (
    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
      {box('ATTESO', euro(r.atteso))}
      {box('INCASSATO', euro(r.incassato), '#22C55E')}
      {box('MANCANO', euro(r.mancano), r.mancano > 0 ? '#EF4444' : undefined)}
      {box('PERSONE', <span style={{ fontSize: '14px', fontWeight: 700 }}>
        <span style={{ color: '#22C55E' }}>{r.pagati} pagati</span> · <span style={{ color: '#F59E0B' }}>{r.parziali} parziali</span> · <span style={{ color: '#EF4444' }}>{r.da_pagare} da pagare</span></span>)}
      {dati.banca && r.con_proposte > 0 && box('DALLA BANCA', <span style={{ fontSize: '14px', fontWeight: 700, color: '#3B82F6', display: 'inline-flex', gap: '6px', alignItems: 'center' }}><Landmark size={14} /> {r.con_proposte} con bonifico proposto</span>)}
    </div>
  );
}

// ── finestra pagamento di una risposta ───────────────────────────────────────────────────
export function FinestraPagamento({ f, riga, banca, puoModificare, onChiudi, onCambio }) {
  const [nuovo, setNuovo] = useState(null); // { importo, data, metodo, note }
  const [quotaMod, setQuotaMod] = useState(null);
  const [lavoro, setLavoro] = useState(false);
  const [err, setErr] = useState('');
  const fai = async (fn) => {
    setLavoro(true); setErr('');
    try { await fn(); await onCambio(); } catch (e) { setErr(e.response?.data?.error || 'Errore'); }
    setLavoro(false);
  };
  const p = riga;
  const salvaNuovo = () => fai(async () => { await api.post(`/form/${f.id}/risposte/${p.risposta_id}/pagamenti`, nuovo); setNuovo(null); });
  const salvaQuota = (q) => fai(async () => { await api.put(`/form/${f.id}/risposte/${p.risposta_id}/quota`, { quota: q }); setQuotaMod(null); });
  const elimina = (x) => window.confirm(`Togliere il pagamento di ${euro(x.importo)} del ${dataIt(x.data)}?${x.bank_transaction_id ? ' Il bonifico torna disponibile.' : ''}`)
    && fai(() => api.delete(`/form/${f.id}/pagamenti/${x.id}`));
  // di un bonifico si collega solo quanto manca (es. 580 € corso + quota: 30 qui, il resto resta per il corso)
  const collega = (b) => fai(() => api.post(`/form/${f.id}/risposte/${p.risposta_id}/pagamenti`, { bank_transaction_id: b.id, importo: Math.min(b.disponibile, p.residuo > 0 ? p.residuo : b.disponibile) }));
  const scarta = (b) => fai(() => api.post(`/form/${f.id}/risposte/${p.risposta_id}/scarta-bonifico`, { bank_transaction_id: b.id }));
  const ripristina = () => fai(() => api.post(`/form/${f.id}/risposte/${p.risposta_id}/ripristina-bonifici`));
  const st = STATI_PAGAMENTO[p.stato] || STATI_PAGAMENTO.DA_PAGARE;

  return (
    <InFinestra attiva onChiudi={onChiudi} larghezza={620}>
      <div style={{ padding: '18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: '17px' }}>{p.nome || 'Risposta'}</div>
            <div style={s.meta}>{f.titolo}</div>
          </div>
          <span style={{ ...s.badge, color: st.c, borderColor: `${st.c}55`, background: `${st.c}12` }}>{st.l}</span>
          <button style={s.btnSec} onClick={onChiudi}><X size={13} /> CHIUDI</button>
        </div>
        {err && <div style={{ ...s.errore, marginBottom: 0 }}>{err}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
          {[['QUOTA', euro(p.quota)], ['PAGATO', euro(p.pagato)], ['MANCA', euro(p.residuo)]].map(([l, v]) => (
            <div key={l} style={{ background: 'var(--bg-input)', borderRadius: '10px', padding: '8px 12px' }}><div style={s.label}>{l}</div><div style={{ fontWeight: 800, fontSize: '17px' }}>{v}</div></div>
          ))}
        </div>
        <div style={{ fontSize: 'var(--font-size-sm)' }}>
          {quotaMod !== null ? (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <span>Quota per questa persona:</span>
              <input style={{ ...s.inp, width: '110px' }} inputMode="decimal" autoFocus value={quotaMod} onChange={e => setQuotaMod(e.target.value)} />
              <button style={s.btnPri} disabled={lavoro} onClick={() => salvaQuota(quotaMod)}><Check size={12} /> SALVA</button>
              <button style={s.btnSec} onClick={() => setQuotaMod(null)}>ANNULLA</button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', color: 'var(--text-dim)' }}>
              {p.manuale ? <span>Quota decisa a mano (l'automatica sarebbe {euro(p.quota_auto)}: {p.motivo_auto}).</span> : <span>{p.motivo_auto}.</span>}
              {puoModificare && <button style={{ ...s.btnSec, padding: '4px 10px' }} onClick={() => setQuotaMod(String(p.quota))}><Pencil size={11} /> CAMBIA QUOTA</button>}
              {puoModificare && p.manuale && <button style={{ ...s.btnSec, padding: '4px 10px' }} disabled={lavoro} onClick={() => salvaQuota(null)}><RotateCcw size={11} /> TORNA AUTOMATICA</button>}
            </div>
          )}
        </div>

        <div>
          <div style={s.cardTitle}>PAGAMENTI</div>
          {!p.pagamenti.length && <div style={s.meta}>Nessun pagamento registrato.</div>}
          {p.pagamenti.map(x => (
            <div key={x.id} style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 'var(--font-size-sm)' }}>
              <span style={{ fontWeight: 800, minWidth: '64px' }}>{euro(x.importo)}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                {dataIt(x.data)} · {(METODI.find(m => m[0] === x.metodo) || [, x.metodo])[1]}
                {x.bank_transaction_id && <span title="Collegato al movimento della banca" style={{ marginLeft: 6, color: '#3B82F6', display: 'inline-flex', verticalAlign: 'middle' }}><Landmark size={12} /></span>}
                {x.note && <span style={{ display: 'block', ...s.meta, overflowWrap: 'anywhere' }}>{x.note}</span>}
                <span style={{ display: 'block', ...s.meta }}>Registrato da {x.creato_da || 'staff'}</span>
              </span>
              {puoModificare && <button style={s.iconBtn} title="Togli" disabled={lavoro} onClick={() => elimina(x)}><Trash2 size={14} /></button>}
            </div>
          ))}
          {puoModificare && !nuovo && <button style={{ ...s.btnSec, marginTop: '10px' }} onClick={() => setNuovo({ importo: p.residuo > 0 ? String(p.residuo) : '', data: oggi(), metodo: 'CONTANTI', note: '' })}><Plus size={12} /> REGISTRA PAGAMENTO</button>}
          {nuovo && (
            <div style={{ marginTop: '10px', padding: '12px', border: '1px solid var(--border2)', borderRadius: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px' }}>
                <div><label style={s.label}>IMPORTO (€)</label><input style={s.inp} inputMode="decimal" value={nuovo.importo} onChange={e => setNuovo(n => ({ ...n, importo: e.target.value }))} /></div>
                <div><label style={s.label}>DATA</label><input type="date" style={s.inp} value={nuovo.data} onChange={e => setNuovo(n => ({ ...n, data: e.target.value }))} /></div>
                <div><label style={s.label}>COME</label><select style={s.inp} value={nuovo.metodo} onChange={e => setNuovo(n => ({ ...n, metodo: e.target.value }))}>{METODI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
              </div>
              <div><label style={s.label}>NOTA (facoltativa)</label><input style={s.inp} value={nuovo.note} onChange={e => setNuovo(n => ({ ...n, note: e.target.value }))} placeholder="es. pagato al campo" /></div>
              {nuovo.metodo === 'CONTANTI' && <p style={{ ...s.help, margin: 0 }}>Contanti: ricordati la ricevuta. Qui resta solo chi ha pagato, la cassa non cambia.</p>}
              {nuovo.metodo === 'BONIFICO' && banca && p.proposte.length > 0 && <p style={{ ...s.help, margin: 0 }}>C'è un bonifico proposto qui sotto: meglio COLLEGA, così resta agganciato alla banca.</p>}
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button style={s.btnSec} onClick={() => setNuovo(null)}>ANNULLA</button>
                <button style={s.btnPri} disabled={lavoro} onClick={salvaNuovo}><Check size={12} /> SALVA</button>
              </div>
            </div>
          )}
        </div>

        {banca && (p.proposte.length > 0 || p.scartati > 0) && (
          <div>
            <div style={{ ...s.cardTitle, color: '#3B82F6', display: 'flex', alignItems: 'center', gap: '6px' }}><Landmark size={13} /> BONIFICI DELLA BANCA CHE SEMBRANO SUOI</div>
            {p.proposte.map(b => (
              <div key={b.id} style={{ border: '1px solid var(--border2)', borderLeft: `3px solid ${LIVELLO[b.livello]}`, borderRadius: '10px', padding: '10px 12px', marginBottom: '8px', fontSize: 'var(--font-size-sm)' }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span style={{ fontWeight: 800 }}>{euro(b.importo)}</span>
                  <span style={s.meta}>{dataIt(b.data)}{b.disponibile < b.importo ? ` · ancora da assegnare ${euro(b.disponibile)}` : ''}</span>
                  <span style={{ marginLeft: 'auto', ...s.badge, color: LIVELLO[b.livello], borderColor: `${LIVELLO[b.livello]}55` }}>{b.livello}</span>
                </div>
                <div style={{ marginTop: '4px', overflowWrap: 'anywhere' }}>{b.causale}</div>
                <div style={s.meta}>Da: {b.ordinante || '—'} · {b.motivi.join(' · ')}</div>
                {puoModificare && <div style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button style={s.btnPri} disabled={lavoro} onClick={() => collega(b)}><Check size={12} /> COLLEGA {euro(Math.min(b.disponibile, p.residuo > 0 ? p.residuo : b.disponibile))}</button>
                  <button style={s.btnSec} disabled={lavoro} onClick={() => scarta(b)} title="Non è suo: non proporlo più a questa persona"><X size={12} /> NON È SUO</button>
                  {b.disponibile > p.residuo && p.residuo > 0 && <span style={s.meta}>Il resto ({euro(b.disponibile - p.residuo)}) resta libero per altro (es. il corso).</span>}
                </div>}
              </div>
            ))}
            {p.scartati > 0 && puoModificare && <button style={{ ...s.btnSec, padding: '4px 10px' }} disabled={lavoro} onClick={ripristina}><RotateCcw size={11} /> RIMETTI I {p.scartati} SCARTATI</button>}
          </div>
        )}
      </div>
    </InFinestra>
  );
}

// ── vista STATISTICHE ────────────────────────────────────────────────────────────────────
const CONTABILI = ['SCELTA_SINGOLA', 'SCELTA_MULTIPLA', 'ELENCO', 'SI_NO', 'VALUTAZIONE'];
const valoriDi = (c, v) => (c.tipo === 'SCELTA_MULTIPLA' ? (Array.isArray(v) ? v : []) : [testoRisposta(c, v)]).map(x => x || '(senza risposta)');

export function Statistiche({ f, risposte, pagamenti }) {
  const domande = useMemo(() => colonneRisposte(f, risposte).filter(c => CONTABILI.includes(c.tipo)), [f, risposte]);
  const [per, setPer] = useState('');
  const dividi = domande.find(c => c.id === per && c.tipo !== 'SCELTA_MULTIPLA');
  const gruppi = useMemo(() => {
    if (!dividi) return null;
    const m = new Map();
    for (const r of risposte) { const g = valoriDi(dividi, r.dati?.[dividi.id])[0]; m.set(g, (m.get(g) || 0) + 1); }
    const ord = (dividi.opzioni || []).filter(o => m.has(o));
    return [...ord, ...[...m.keys()].filter(k => !ord.includes(k)).sort()];
  }, [dividi, risposte]);

  const conteggi = (c) => {
    const tot = new Map();
    const perG = new Map();
    for (const r of risposte) {
      const g = dividi ? valoriDi(dividi, r.dati?.[dividi.id])[0] : null;
      for (const v of valoriDi(c, r.dati?.[c.id])) {
        tot.set(v, (tot.get(v) || 0) + 1);
        if (g !== null) { const k = `${v}|${g}`; perG.set(k, (perG.get(k) || 0) + 1); }
      }
    }
    const ord = (c.opzioni || []).filter(o => tot.has(o));
    const chiavi = [...ord, ...[...tot.keys()].filter(k => !ord.includes(k)).sort((a, b) => (a === '(senza risposta)' ? 1 : b === '(senza risposta)' ? -1 : a.localeCompare(b, 'it')))];
    return { chiavi, tot, perG };
  };

  const incassi = useMemo(() => {
    if (!pagamenti?.attivo) return null;
    const perQuota = new Map(), perMetodo = new Map();
    for (const x of Object.values(pagamenti.righe)) {
      const q = perQuota.get(x.quota) || { n: 0, pagati: 0 };
      q.n++; if (x.stato === 'PAGATO') q.pagati++;
      perQuota.set(x.quota, q);
      for (const p of x.pagamenti) perMetodo.set(p.metodo, (perMetodo.get(p.metodo) || 0) + p.importo);
    }
    return { perQuota: [...perQuota.entries()].sort((a, b) => a[0] - b[0]), perMetodo: [...perMetodo.entries()] };
  }, [pagamenti]);

  const cella = { padding: '6px 10px', borderBottom: '1px solid var(--border)', fontSize: 'var(--font-size-sm)', textAlign: 'right', whiteSpace: 'nowrap' };
  return <>
    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
      <span style={s.meta}>{risposte.length} risposte (tutte, senza filtri)</span>
      <span style={{ marginLeft: 'auto', fontSize: 'var(--font-size-sm)' }}>Dividi per</span>
      <select style={{ ...s.inp, width: 'auto', maxWidth: '320px' }} value={per} onChange={e => setPer(e.target.value)}>
        <option value="">— niente —</option>
        {domande.filter(c => c.tipo !== 'SCELTA_MULTIPLA').map(c => <option key={c.id} value={c.id}>{c.etichetta}</option>)}
      </select>
    </div>
    {incassi && (
      <div style={s.card}>
        <div style={s.cardTitle}>INCASSI</div>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', fontSize: 'var(--font-size-sm)' }}>
          <div>{incassi.perQuota.map(([q, v]) => <div key={q}>Quota <b>{euro(q)}</b>: {v.n} persone, {v.pagati} pagato tutto</div>)}</div>
          <div>{incassi.perMetodo.length ? incassi.perMetodo.map(([m, v]) => <div key={m}>{(METODI.find(x => x[0] === m) || [, m])[1]}: <b>{euro(v)}</b></div>) : <span style={s.meta}>Ancora nessun incasso.</span>}</div>
        </div>
      </div>
    )}
    {!domande.length && <div style={s.vuoto}>Nessuna domanda a scelta da contare in questo form.</div>}
    {domande.filter(c => c.id !== dividi?.id).map(c => {
      const { chiavi, tot, perG } = conteggi(c);
      const max = Math.max(1, ...tot.values());
      return (
        <div key={c.id} style={s.card}>
          <div style={{ fontWeight: 700, marginBottom: '10px' }}>{c.etichetta}{c.tipo === 'SCELTA_MULTIPLA' && <span style={s.meta}> · più risposte per persona</span>}</div>
          {!gruppi ? chiavi.map(k => (
            <div key={k} style={{ display: 'grid', gridTemplateColumns: 'minmax(90px, 220px) 1fr 44px', gap: '10px', alignItems: 'center', marginBottom: '5px', fontSize: 'var(--font-size-sm)' }}>
              <span style={{ overflowWrap: 'anywhere', color: k === '(senza risposta)' ? 'var(--text-dim)' : undefined }}>{k}</span>
              <span style={{ height: '12px', borderRadius: '6px', background: 'var(--bg-input)', overflow: 'hidden' }}><span style={{ display: 'block', height: '100%', width: `${(tot.get(k) / max) * 100}%`, background: '#F4600C', borderRadius: '6px' }} /></span>
              <b style={{ textAlign: 'right' }}>{tot.get(k)}</b>
            </div>
          )) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead><tr>
                  <th style={{ ...cella, textAlign: 'left' }} />
                  {gruppi.map(g => <th key={g} style={{ ...cella, fontSize: 'var(--font-size-xs)', fontWeight: 700 }}>{g}</th>)}
                  <th style={{ ...cella, fontSize: 'var(--font-size-xs)', fontWeight: 800 }}>TOTALE</th>
                </tr></thead>
                <tbody>{chiavi.map(k => (
                  <tr key={k}>
                    <td style={{ ...cella, textAlign: 'left', whiteSpace: 'normal' }}>{k}</td>
                    {gruppi.map(g => <td key={g} style={{ ...cella, color: perG.get(`${k}|${g}`) ? undefined : 'var(--text-dim)' }}>{perG.get(`${k}|${g}`) || '·'}</td>)}
                    <td style={{ ...cella, fontWeight: 800 }}>{tot.get(k)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      );
    })}
  </>;
}
